import type { ReactNode } from 'react';
import { Skeleton } from '~/components/ui/skeleton';
import { cn } from '~/lib/utils';

export type ListFooterContext = {
  isLoadingMore: boolean;
  itemClassName?: string;
};

/** The column every list lives in: fixed width, its own scroll, ruled off from
 *  the detail beside it. */
export function ListPanel({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-full w-96 shrink-0 flex-col overflow-hidden border-r border-border bg-background">
      {children}
    </div>
  );
}

export function ListSkeleton({
  className,
  count = 8,
  itemClassName,
  label,
}: {
  className?: string;
  count?: number;
  itemClassName?: string;
  label?: string;
}) {
  const role = label ? 'status' : undefined;

  return (
    <div role={role} aria-label={label} className={className}>
      {Array.from({ length: count }, (_, index) => (
        <Skeleton key={index} className={itemClassName} />
      ))}
    </div>
  );
}

export function EmptyListPanel({
  className,
  title,
}: {
  className?: string;
  title: string;
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-center px-6 text-center',
        className,
      )}
    >
      <p className="text-sm text-muted-foreground">{title}</p>
    </div>
  );
}

export function UnreadIndicator({ isUnread }: { isUnread: boolean }) {
  return (
    <span className={cn('size-2', isUnread && 'rounded-full bg-primary')} />
  );
}

/**
 * The `Footer` slot every virtualised list passes to Virtuoso: a skeleton row
 * while the next page loads, otherwise trailing padding.
 */
export function LoadMoreFooter({ context }: { context?: ListFooterContext }) {
  if (context?.isLoadingMore) {
    return (
      <ListSkeleton
        className="px-4 pb-4"
        itemClassName={cn('mb-2 w-full rounded-xl', context.itemClassName)}
      />
    );
  }

  return <div className="h-2" aria-hidden="true" />;
}

export const paginatedListComponents = { Footer: LoadMoreFooter };

export function DetailsLoading() {
  return (
    <div className="flex min-w-0 flex-1 flex-col overflow-hidden bg-background">
      <div className="border-b border-border px-5 py-4">
        <Skeleton className="h-5 w-64" />
        <Skeleton className="mt-2 h-4 w-40" />
      </div>
      <div className="space-y-4 p-5">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-36 w-full" />
        <Skeleton className="h-36 w-full" />
      </div>
    </div>
  );
}

export function EmptyDetails({
  description,
  title,
}: {
  description: string;
  title: string;
}) {
  return (
    <div className="flex min-w-0 flex-1 items-center justify-center bg-background p-8 text-center">
      <div>
        <p className="text-lg font-medium text-foreground">{title}</p>
        <p className="mt-2 text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}

/** Exact up to 99, "99+" beyond — the read layer stops counting at 100. */
export function formatUnreadCount(count: number) {
  return count > 99 ? '99+' : String(count);
}
